<template>
  <div>
    <vx-card class="mb-5" title="Learning & Resources">
      <p class="mb-5">
        Watch this video to gain an insight into what it’s like working as a personal trainer
      </p>
      <responsive-youtube-wrapper :video="intro_videos[1].video" :image="intro_videos[1].image" />
    </vx-card>
    <vx-card title="Qualities of a Fitness Instructor">
      <div class="flex mb-5">
        <p class="w-1/2">Rate yourself out of 5 for the following important qualities:</p>
        <div class="flex items-center justify-between w-1/2">
          <strong>Improvement Required</strong>
          <strong>Confident</strong>
        </div>
      </div>

      <table class="table default-table">
        <thead>
          <tr class="subheader">
            <th width="50%"></th>
            <th class="text-center" v-for="item in 5" :key="'qh_' + item" width="10%">
              {{ item }}
            </th>
          </tr>
        </thead>
        <tr class="body" v-for="(item, index) in rate_items" :key="'rate_item_' + index">
          <th scope="row" width="50%" class="subheader text-left px-5">{{ item }}</th>
          <td class="text-center" v-for="item in 5" :key="'q_' + item" width="10%">
            <vs-radio
              :vs-name="'answer_1' + index"
              v-model="data['question' + index]"
              class="checkbox_answer multiline"
              :vs-value="item"
              :disabled="!isEditable"
              :aria-label="`${rate_items[index]}: rating ${item}`"
            ></vs-radio>
          </td>
        </tr>
      </table>
      <p class="mb-5">Which of these qualities do you need to work on the most?</p>
      <text-block :editable="true" v-model="data.question6"></text-block>

      <button class="btn btn-primary btn-size-medium mr-3" @click="save()" :disabled="disableSave">
        Save
      </button>
      <vs-progress indeterminate color="primary" v-if="showSaveProgress"></vs-progress>
    </vx-card>
    <vx-card title="The 3 Types of Exercises">
      <p class="mb-5">
        As a fitness instructor or personal trainer, you will need to be aware that exercise comes
        in 3 main flavours:
      </p>
      <ul class="default-list">
        <li>
          <strong>Cardio Training</strong> - this type of exercise is great for losing weight,
          strengthening the heart and lungs, and improving mood. Running, cycling, and skipping are
          all examples of cardio-intensive exercise.<br /><br />
          Learn more about cardio training in the video below:<br /><br />
          <responsive-youtube-wrapper
            :video="intro_videos[2].video"
            :image="intro_videos[2].image"
          />
        </li>
        <li>
          <strong>Strength Training</strong> - this type of exercise can increase the metabolism to
          burn calories, increase muscle mass and bone density, keep joints supple. Lifting weights
          and bodyweight workouts are examples of strength training.<br /><br />
          Learn more about strength training in the video below:<br /><br />
          <responsive-youtube-wrapper
            :video="intro_videos[3].video"
            :image="intro_videos[3].image"
          />
        </li>
        <li>
          <strong>Flexibility Training</strong> - this type of exercise improves physical
          performance, helps with recovery, and helps prevent injury. Stretching and yoga are
          examples of flexibility training.<br /><br />
          Learn more about flexibility training in the video below:<br /><br />
          <responsive-youtube-wrapper
            :video="intro_videos[4].video"
            :image="intro_videos[4].image"
          />
        </li>
      </ul>
    </vx-card>
    <vx-card title="How to Create a Workout Plan">
      <p class="mb-5">
        One of your main duties when working as a fitness instructor or personal trainer is
        designing workout programmes to suit a client’s needs. Later in this virtual job experience,
        you will get some practice in designing your own workouts.
      </p>
      <p class="mb-5">
        But, to learn a little more about the process and what to consider when designing a workout,
        watch the video below. Take notes if you need to!
      </p>
      <responsive-youtube-wrapper :video="intro_videos[5].video" :image="intro_videos[5].image" />
    </vx-card>
    <vx-card>
      <p class="mb-5">And remember, what not to do is often as important as what to do:</p>
      <responsive-youtube-wrapper :video="intro_videos[6].video" :image="intro_videos[6].image" />
    </vx-card>
  </div>
</template>

<script>
import methods from '@/views/WorkExperiences/components/lr_js/methods';
import computed from '@/views/WorkExperiences/components/lr_js/computed';
import inject from '@/views/WorkExperiences/components/lr_js/inject';
import store from '@/store/store';

export default {
  data() {
    return {
      disableSave: false,
      showSaveProgress: false,
      intro_videos: {
        1: {
          image: require('@/assets/images/experiences/fitness/vid_1_splash.jpg'),
          video: 'https://www.youtube.com/embed/FTuTXoIL27w',
        },
        2: {
          image: require('@/assets/images/experiences/fitness/vid_2_splash.jpg'),
          video: 'https://www.youtube.com/embed/8sAWJdQtGbE',
        },
        3: {
          image: require('@/assets/images/experiences/fitness/vid_3_splash.jpg'),
          video: 'https://www.youtube.com/embed/yq3-S1JCytA',
        },
        4: {
          image: require('@/assets/images/experiences/fitness/vid_4_splash.jpg'),
          video: 'https://www.youtube.com/embed/hqEbkpKztxw',
        },
        5: {
          image: require('@/assets/images/experiences/fitness/vid_5_splash.jpg'),
          video: 'https://www.youtube.com/embed/aY-jST9htEM',
        },
        6: {
          image: require('@/assets/images/experiences/fitness/vid_6_splash.jpg'),
          video: 'https://www.youtube.com/embed/vS0k8Wq5CCg',
        },
      },
      rate_items: [
        'A positive attitude and an approachable personality',
        'The ability to teach and motivate people of all ages and backgrounds - good communication skills for explaining techniques and movements clearly',
        'A good level of physical fitness',
        'A responsible attitude to health and safety',
        'A flexible attitude to work',
      ],
      showVideoPopupYt: false,
      ytVideoUrl: false,
    };
  },
  methods: methods,
  computed: {
    ...computed,
    activeExperienceID() {
      return store.getters['authStudent/activeExperienceID'];
    },
    experience() {
      const loginType = localStorage.getItem('login_type');
      if (loginType == 'student') {
        const experiences = store.getters['authStudent/experiences'];
        return experiences.find((i) => i.id == this.activeExperienceID);
      } else {
        return store.getters['authTeacher/experience'];
      }
    },
  },
  inject: inject,
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
